import {
  FC,
  MutableRefObject,
  forwardRef,
  useEffect,
  useRef,
  useState,
  FocusEvent as ReactFocusEvent,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
} from 'react';
import classNames from 'classnames/bind';
import { MLPVersion, Theme } from '../../types/enums';
import { Breakpoints, useBreakpoint } from '../Breakpoint/BreakpointProvider';
import Button, { ButtonSize, ButtonStyles, ButtonTypes, ButtonWidth } from '../Button/Button';
import Drawer from '../Drawer/Drawer';
import { FAIcon } from '../Icons/Icons';
import Tooltip, { TooltipPreference } from '../Tooltip/Tooltip';
import { useClickOutsideDetector } from '../../hooks';
import { useTranslation } from '../Translation/TranslationProvider';
import FloatLabelInput from '../Input/FloatLabelInput/FloatLabelInput';
import { InputValidations } from '../Input/Input';
import Calendar, { CalendarYearDropdownType } from './Calendar/Calendar';
import { timeInFormat } from './Calendar/CalendarHelper';
import styles from './DatePicker.module.scss';
import { assignIfExists, getDateTimeText } from './DatePickerHelper';
import TimePicker from './TimePicker/TimePicker';
import { CalendarWidth, DatePickerType, DatePickerValidataionStatus } from './types/enums';
import {
  type RefType,
  type TimePickerProps,
  ComponentUserEvent,
  DatePickerProps,
  PLACEHOLDER_TEXT,
} from './types/propTypes';

const cx = classNames.bind(styles);

const RenderComponent = ({
  mlpVersion,
  ariaLabelValue,
  disabled,
  isInputFocused,
  componentUserEvent,
  status,
  placeholder,
  value,
  buttonClassname,
  inputLabel,
  required,
  labelInfo,
  labelInfoPreference,
  inputContainerHeight,
}: {
  mlpVersion: MLPVersion;
  ariaLabelValue: string;
  disabled: boolean;
  isInputFocused: boolean;
  componentUserEvent: ComponentUserEvent;
  status: DatePickerValidataionStatus;
  placeholder: string;
  value: string;
  buttonClassname: string;
  inputLabel?: string;
  required?: boolean;
  labelInfo?: string;
  labelInfoPreference?: TooltipPreference;
  inputContainerHeight?: string;
}) => {
  const currentStatus = isInputFocused ? {} : { status: status as unknown as InputValidations };
  return mlpVersion === MLPVersion.TWO ? (
    <FloatLabelInput
      name="datepicker-mlpversion-2"
      aria-label={ariaLabelValue}
      disabled={disabled}
      label={inputLabel}
      isInputFocused={isInputFocused}
      {...componentUserEvent}
      {...currentStatus}
      placeholder={placeholder}
      value={value}
      inputIcon={{ iconName: 'calendar' }}
    />
  ) : (
    <>
      {inputLabel && (
        <p className={styles['input-label']}>
          {inputLabel}
          {required && (
            <span className={styles['required-indicator']} data-testid="date-picker-required">
              *
            </span>
          )}
          {labelInfo && (
            <Tooltip message={labelInfo} preference={labelInfoPreference} className={styles['date-picker-tool-tip']} />
          )}
        </p>
      )}
      <button
        type="button"
        className={buttonClassname}
        style={inputContainerHeight ? { height: inputContainerHeight } : {}}
        data-testid="input-container"
        {...componentUserEvent}
        tabIndex={0}
        aria-label={ariaLabelValue}
      >
        <span className={styles['placeholder-container']} data-value={value || PLACEHOLDER_TEXT}>
          {value || placeholder}
        </span>
        <FAIcon iconName="calendar" className={styles['calendar-icon']} />
      </button>
    </>
  );
};
const DatePicker: FC<DatePickerProps> = forwardRef<HTMLDivElement, DatePickerProps>(
  (
    {
      onChange,
      inputLabel,
      labelInfo = '',
      labelInfoPreference = TooltipPreference.RIGHT,
      placeholder = '',
      cancelLabel,
      scheduleLabel,
      onCalendarToggle,
      onSelect,
      isWithinModal = false,
      displayText,
      description,
      calendarHeading,
      calendarFooter,
      inputContainerHeight,
      ariaLabel,
      disabled = false,
      isWithinDrawer = false,
      calendarWidth = CalendarWidth.DEFAULT,
      onClose,
      type = DatePickerType.DROP_DOWN,
      status = DatePickerValidataionStatus.DEFAULT,
      value,
      valueTo,
      startTimeValue,
      endTimeValue,
      is24HoursFormat = true,
      required = false,
      mlpVersion = MLPVersion.ONE,
      ...restProps
    },
    ref,
  ) => {
    // State
    const [showCalendar, setShowCalendar] = useState(false);
    const [selectedDate, setSelectedDate] = useState(value);
    const [selectedEndDate, setSelectedEndDate] = useState(valueTo);

    const [selectedStartTime, setSelectedStartTime] = useState<string | undefined>();
    const [selectedEndTime, setSelectedEndTime] = useState<string | undefined>();
    const [displayValue, setDisplayValue] = useState(
      getDateTimeText(value, valueTo, startTimeValue, endTimeValue, restProps.showTime, restProps.selectRange),
    );

    const t = useTranslation();
    const ariaLabelValue = ariaLabel || t('nextgen.Datepicker.ariaLabel');

    // update value if props updated after render
    useEffect(() => {
      setSelectedDate(value);
      const currentStartTime = timeInFormat(is24HoursFormat || false, startTimeValue);
      restProps.showTime && setSelectedStartTime(currentStartTime);
      let currentEndTime = endTimeValue;
      if (restProps.selectRange) {
        setSelectedEndDate(valueTo);
        if (restProps.showTime) {
          currentEndTime = timeInFormat(is24HoursFormat || false, endTimeValue);
          setSelectedEndTime(currentEndTime);
        }
      }
      setDisplayValue(
        getDateTimeText(value, valueTo, currentStartTime, currentEndTime, restProps.showTime, restProps.selectRange),
      );
    }, [value, valueTo, startTimeValue, endTimeValue]);
    // Datepicker ref to identify user interactions.
    const datePickerRef = useRef() as MutableRefObject<HTMLDivElement>;
    const calendarRef = useRef<RefType>(null);
    const viewPortType = useBreakpoint();
    const isSmallDevice = () => {
      const breakpointsArr: string[] = [Breakpoints.XS, Breakpoints.SM];
      return breakpointsArr.includes(viewPortType);
    };

    useEffect(() => {
      // converts time format once page loaded
      if (type !== DatePickerType.MODAL || !is24HoursFormat) {
        const currentStartTime = timeInFormat(false, startTimeValue);
        const currentEndTime = timeInFormat(false, endTimeValue);
        setSelectedStartTime(currentStartTime);
        setSelectedEndTime(currentEndTime);
        setDisplayValue(
          getDateTimeText(value, valueTo, currentStartTime, currentEndTime, restProps.showTime, restProps.selectRange),
        );
      }
    }, []);
    const outsideClickAction = () => {
      if (type === DatePickerType.DROP_DOWN) {
        setShowCalendar(false);
      }
    };
    useClickOutsideDetector(datePickerRef, outsideClickAction);
    function handleDateTimeChange(
      currentSelectedDate?: Date,
      currentSelectedEndDate?: Date,
      currentSelectedStartTime?: string,
      currentSelectedEndTime?: string,
      event?: ReactKeyboardEvent | ReactMouseEvent | ReactFocusEvent,
    ) {
      if (restProps.selectRange) {
        setSelectedDate(currentSelectedDate);
        setSelectedEndDate(currentSelectedEndDate);
        setSelectedStartTime(currentSelectedStartTime);
        setSelectedEndTime(currentSelectedEndTime);
      } else {
        assignIfExists(currentSelectedDate, setSelectedDate);
        assignIfExists(currentSelectedEndDate, setSelectedEndDate);
        assignIfExists(currentSelectedStartTime, setSelectedStartTime);
        assignIfExists(currentSelectedEndTime, setSelectedEndTime);
      }
      if (type === DatePickerType.MODAL) {
        setSelectedStartTime(currentSelectedStartTime);
        setSelectedEndTime(currentSelectedEndTime);
        setDisplayValue(
          getDateTimeText(
            currentSelectedDate,
            currentSelectedEndDate,
            currentSelectedStartTime,
            currentSelectedEndTime,
            restProps.showTime,
            restProps.selectRange,
          ),
        );
        onChange?.(
          currentSelectedDate,
          currentSelectedEndDate,
          currentSelectedStartTime,
          currentSelectedEndTime,
          event,
        );
      }
      onSelect?.(event);
    }
    function handleCalendarVisibility() {
      const tempState = !showCalendar;
      setShowCalendar(tempState);
      if (tempState) {
        onClose?.();
      }
    }
    function handleCancelBtnClick(event: ReactMouseEvent) {
      handleDateTimeChange(undefined, undefined, undefined, undefined, event);
      handleCalendarVisibility();
    }
    function handleScheduleClick(event: ReactKeyboardEvent | ReactMouseEvent | ReactFocusEvent) {
      setDisplayValue(
        getDateTimeText(
          selectedDate,
          selectedEndDate,
          selectedStartTime,
          selectedEndTime,
          restProps.showTime,
          restProps.selectRange,
        ),
      );
      onChange?.(selectedDate, selectedEndDate, selectedStartTime, selectedEndTime, event);
      handleCalendarVisibility();
    }

    const actionButtons = (
      <div className={styles['action-button-container']}>
        <span className={styles['action-button']}>
          <Button
            title={cancelLabel}
            type={ButtonTypes.BUTTON}
            theme={Theme.BLACK}
            size={ButtonSize.SMALL}
            buttonStyle={ButtonStyles.OUTLINE}
            onClick={handleCancelBtnClick}
            buttonWidth={ButtonWidth.FULL_WIDTH}
          />
        </span>
        <span className={`${styles['action-button']} ${styles['primary-action-button']}`}>
          <Button
            title={scheduleLabel}
            type={ButtonTypes.BUTTON}
            theme={Theme.BLACK}
            size={ButtonSize.SMALL}
            buttonStyle={ButtonStyles.SOLID}
            onClick={handleScheduleClick}
            data-testid="schedule-btn"
            buttonWidth={ButtonWidth.FULL_WIDTH}
          />
        </span>
      </div>
    );
    const closeIconComponent = onClose ? (
      <FAIcon
        iconName="xmark"
        className={styles['icon-close']}
        onClick={handleCalendarVisibility}
        data-testid="heading-close-icon"
      />
    ) : (
      <></>
    );
    const descriptionContent = (
      <div className={styles['calendar-header']}>
        {!isSmallDevice() && (
          <h3 className={styles['calendar-header-title']}>
            {calendarHeading}
            {closeIconComponent}
          </h3>
        )}
        {description && (
          <p className={`${styles['description-container']} ${styles['datePicker-modal-description-container']}`}>
            {description}
          </p>
        )}
      </div>
    );

    const calendarComponent = (
      <div className={styles['calendar-body']}>
        <Calendar
          {...restProps}
          onChange={handleDateTimeChange}
          ref={calendarRef}
          forwardedRef={ref}
          type={isSmallDevice() ? DatePickerType.DROP_DOWN : type}
          calendarHeading={calendarHeading}
          onClose={handleCalendarVisibility}
          value={selectedDate}
          valueTo={selectedEndDate}
          startTimeValue={selectedStartTime}
          endTimeValue={selectedEndTime}
          is24HoursFormat={is24HoursFormat}
        />
      </div>
    );

    // Calendar CSS Classes
    const calendarDefaultCls = `${styles['menu-container']} ${styles[calendarWidth]} ${
      isWithinDrawer ? styles['within-drawer'] : ''
    }`;
    const calendarModalCls = styles['calendar-modal-container'];
    const footerContent = <>{calendarFooter && <div className={styles['calendar-footer']}>{calendarFooter}</div>}</>;
    const fallbackContent = showCalendar ? <span className={calendarModalCls}>{calendarComponent}</span> : <></>;
    const defaultContent =
      isSmallDevice() || type === DatePickerType.DROP_DOWN ? (
        <div className={calendarDefaultCls}>
          {descriptionContent}
          {calendarComponent}
          {actionButtons}
        </div>
      ) : (
        fallbackContent
      );

    const drawerContent = showCalendar ? (
      <Drawer
        header={<span>{inputLabel || calendarHeading}</span>}
        footer={<>{actionButtons}</>}
        closeIcon
        onClose={() => setShowCalendar(false)}
      >
        {descriptionContent}
        <div className={styles['calendar-wrapper']}>{calendarComponent}</div>
        {footerContent}
      </Drawer>
    ) : (
      ''
    );
    const ContainerClassname = cx({
      'date-picker-container-within-modal': isWithinModal,
      'date-picker-container': !isWithinModal,
      'calendar-state-open': showCalendar,
    });
    const ButtonClassname = cx(
      'input-container',
      {
        'input-container-active': showCalendar,
      },
      [`datepicker-${status}`],
      { 'datepicker-input-disabled': disabled },
    );
    const componentUserEvent = {
      onClick: (e: ReactMouseEvent<HTMLInputElement | HTMLButtonElement, MouseEvent>) => {
        onCalendarToggle?.(e);
        handleCalendarVisibility();
      },
      onKeyDown: (e: ReactKeyboardEvent<HTMLInputElement | HTMLButtonElement>) => {
        onCalendarToggle?.(e);
        handleCalendarVisibility();
      },
    };
    return (
      <>
        <div
          className={ContainerClassname}
          data-testid="date-picker-container"
          id="nextgen-datepicker-component"
          ref={datePickerRef}
        >
          {RenderComponent({
            mlpVersion,
            ariaLabelValue,
            disabled,
            isInputFocused: showCalendar,
            componentUserEvent,
            status,
            placeholder,
            buttonClassname: ButtonClassname,
            value: displayText || displayValue,
            inputLabel,
            required,
            inputContainerHeight,
          })}
          {isSmallDevice() ? drawerContent : defaultContent}
        </div>
      </>
    );
  },
);
DatePicker.displayName = 'DatePicker';
export { Calendar, CalendarYearDropdownType, CalendarWidth, DatePickerType, TimePicker, DatePickerValidataionStatus };
export type { TimePickerProps };
export default DatePicker;
